import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getHebrewDate } from '@/utils/pageUtils';

interface YahrzeitEntry {
  id: string;
  name: string;
  father_name: string | null;
  hebrew_day?: string;
  hebrew_month?: string;
  gregorian_date?: string;
  notes?: string;
  user_id: string;
  status: string;
  gender?: string;
}

interface YahrzeitDisplayProps {
  content: {
    previewMode?: boolean;
    mockData?: YahrzeitEntry[];
  };
  isPreview?: boolean;
}

// Extract just day number and month name for comparison
const extractDayMonth = (date: string): { day: string, month: string } => {
  if (!date) return { day: '', month: '' };
  
  // Remove vowel marks and normalize spaces
  const normalized = date
    .normalize('NFD')
    .replace(/[\u0591-\u05BD\u05BF-\u05C7]/g, '')  // Remove nikkud
    .replace(/\s+/g, ' ')
    .trim();
  
  // Extract day - take only the actual Hebrew letters, ignore quotes
  let day = '';
  const dayMatch = normalized.match(/^([אבגדהוזחטיכלמנסעפצקרשת])([אבגדהוזחטיכלמנסעפצקרשת])?/);
  if (dayMatch) {
    day = dayMatch[1] + (dayMatch[2] || '');
  }
  
  // Extract month - look for known month names
  let month = '';
  const monthNames = ['ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול', 'תשרי', 'חשון', 'חשוון', 'כסלו', 'טבת', 'שבט', 'אדר'];
  
  for (const monthName of monthNames) {
    if (normalized.includes(monthName)) {
      month = monthName === 'חשוון' ? 'חשון' : monthName;  // Normalize חשוון to חשון
      break;
    }
  }
  
  return { day, month };
};

// Simple function to compare two dates by day and month only
const doDatesMatch = (date1: string, date2: string): boolean => {
  const { day: day1, month: month1 } = extractDayMonth(date1);
  const { day: day2, month: month2 } = extractDayMonth(date2);
  
  const monthsMatch = month1 === month2 && month1 !== '';
  const daysMatch = day1 === day2 && day1 !== '';
  
  return daysMatch && monthsMatch;
};

// Helper function to get dates for the current week (Sunday to Saturday)
const getWeekDates = async (): Promise<string[]> => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 is Sunday, 6 is Saturday
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - currentDay); // Go back to Sunday

  // Fetch all 7 dates in parallel
  const weekDates = await Promise.all(
    Array.from({ length: 7 }).map((_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return getHebrewDate(date);
    })
  );
  return weekDates.filter(Boolean);
};

// Helper to extract just day and month from a normalized Hebrew date
const getDayMonth = (normalized: string) => {
  // Only take the first two parts (day and month)
  const parts = normalized.split(' ');
  return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : normalized;
};

// Helper to get the Sunday of the current week as YYYY-MM-DD
const getWeekStartKey = () => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 is Sunday
  const startOfWeek = new Date(today);
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(today.getDate() - currentDay);
  return startOfWeek.toISOString().split('T')[0];
};

// Helper to extract day and month from either new or legacy fields
const getEntryHebrewDayMonth = (entry: any) => {
  if (entry.hebrew_day && entry.hebrew_month) {
    return { day: entry.hebrew_day, month: entry.hebrew_month };
  }
  if (entry.hebrew_date) {
    const parts = entry.hebrew_date.split(' ');
    if (parts.length >= 2) {
      return { day: parts[0], month: parts[1] };
    }
  }
  return { day: '', month: '' };
};

// Helper to normalize Hebrew month names (remove prefix, diacritics, etc.)
const normalizeHebrewMonth = (month: string) => {
  if (!month) return '';
  // Remove diacritics
  let normalized = month.normalize('NFD').replace(/[\u0591-\u05C7]/g, '');
  // Remove prefix like 'ב'
  normalized = normalized.replace(/^ב[\u05B0-\u05BC]?/, '');
  // Remove spaces
  normalized = normalized.replace(/\s+/g, '');
  return normalized;
};

// Helper to normalize Hebrew quotes
const normalizeHebrewQuotes = (text: string) => {
  if (!text) return '';
  // Replace straight quotes with Hebrew quotes
  return text.replace(/"/g, '״');
};

// Update version number to force cache refresh
const CACHE_VERSION = '3';  // Incremented from 2 to 3

const YahrzeitDisplayPage: React.FC<YahrzeitDisplayProps> = ({ content, isPreview = false }) => {
  const [entries, setEntries] = useState<YahrzeitEntry[]>([]);
  const [weekDates, setWeekDates] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [allYahrzeits, setAllYahrzeits] = useState<YahrzeitEntry[]>([]);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const { user } = useAuth();

  useEffect(() => {
    // Load image to get its dimensions
    const img = new Image();
    img.src = "/images/candle_card_no_background.jpg";
    img.onload = () => {
      setImageSize({
        width: img.width,
        height: img.height
      });
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const weekKey = getWeekStartKey();
        const cacheKey = `yahrzeit-week-${weekKey}-v${CACHE_VERSION}`;
        
        // Clear ALL yahrzeit cache entries
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('yahrzeit-week-')) {
            localStorage.removeItem(key);
            console.log('Cleared cache:', key);
          }
        });

        // Get all dates for the current week
        const dates = await getWeekDates();
        console.log('Fresh week dates:', dates);
        setWeekDates(dates);
        
        if (!user) {
          console.log('No user logged in');
          setLoading(false);
          return;
        }

        // Fetch approved yahrzeits for the user
        const { data: yahrzeits, error } = await supabase
          .from('yahrzeits')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'approved')
          .order('gregorian_date', { ascending: false });
        
        if (error) {
          console.error('Database error:', error);
          throw error;
        }

        console.log('Raw yahrzeits from DB:', yahrzeits);
        
        let weeklyYahrzeits: YahrzeitEntry[] = [];
        if (yahrzeits && yahrzeits.length > 0) {
          // Find yahrzeits matching any date in the current week
          weeklyYahrzeits = yahrzeits.filter(y => {
            console.log('\nAnalyzing yahrzeit:', {
              name: y.name,
              hebrew_day: (y as any).hebrew_day,
              hebrew_month: (y as any).hebrew_month,
              hebrew_date: y.hebrew_date,
              gregorian_date: y.gregorian_date
            });

            const { day, month } = getEntryHebrewDayMonth(y);
            console.log('Extracted day/month:', { day, month });
            
            if (!day || !month) {
              console.log('❌ Skipping - missing day or month');
              return false;
            }

            const entryMonthNorm = normalizeHebrewMonth(month);
            const entryDayNorm = normalizeHebrewQuotes(day);
            console.log('Normalized values:', { 
              day: entryDayNorm, 
              month: entryMonthNorm 
            });
            
            let foundMatch = false;
            dates.forEach(weekDate => {
              const parts = weekDate.split(' ');
              if (parts.length < 2) {
                console.log('Invalid week date format:', weekDate);
                return;
              }
              const [weekDay, weekMonth] = parts;
              const weekMonthNorm = normalizeHebrewMonth(weekMonth);
              console.log('Comparing with week date:', {
                weekDate,
                entry: { day: entryDayNorm, month: entryMonthNorm },
                week: { day: weekDay, month: weekMonthNorm }
              });
              
              if (entryDayNorm === weekDay && entryMonthNorm === weekMonthNorm) {
                console.log('✅ MATCH FOUND for', y.name);
                foundMatch = true;
              }
            });
            
            return foundMatch;
          });
        }

        console.log('\nFinal matches:', weeklyYahrzeits);
        setEntries(weeklyYahrzeits);
        
        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify({ weekDates: dates, entries: weeklyYahrzeits }));
      } catch (err) {
        console.error('Error fetching yahrzeits:', err);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, content, isPreview]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4">
      <h2 className="text-3xl font-bold mb-6 text-center px-8 py-4 bg-white/70 rounded-2xl shadow-md inline-block" dir="rtl">
        אזכרות השבוע
      </h2>
      
      {entries.length === 0 ? (
        <Card className="w-full max-w-2xl bg-opacity-80" style={{ 
          backgroundColor: 'rgba(255, 250, 240, 0.9)',
          backgroundSize: 'cover' 
        }}>
          <CardContent className="p-6 text-center">
            <p className="text-xl" dir="rtl">אין אזכרות השבוע</p>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full flex justify-center">
          <div className="grid w-fit grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 justify-items-center">
            {entries.map((entry) => (
              <Card
                key={entry.id}
                className="transition-all duration-300 hover:shadow-lg bg-white/60 backdrop-blur-sm rounded-xl flex flex-col items-center justify-between relative overflow-hidden w-[500px] h-[420px]"
              >
                <CardContent className="flex flex-col items-center justify-between h-full p-16 relative z-10">
                  <img
                    src="/images/candle_card_no_background.jpg"
                    alt="Memorial Candle"
                    className="absolute left-0 right-0 mx-auto w-full h-auto min-h-full object-cover opacity-30 z-0"
                  />
                  <h3 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-center font-extrabold text-black mb-6 tracking-wide drop-shadow-lg" dir="rtl">
                    לע״נ {entry.name}
                  </h3>
                  {entry.father_name && (
                    <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-center font-extrabold text-black leading-relaxed tracking-wide drop-shadow-lg" dir="rtl">
                      {entry.gender === 'female' ? 'בת' : 'בן'} {entry.father_name}
                    </p>
                  )}
                  {(() => {
                    const { day, month } = getEntryHebrewDayMonth(entry);
                    return (day && month) ? (
                      <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center font-extrabold text-black mt-4 drop-shadow-lg" dir="rtl">
                        {day} {month}
                      </p>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default YahrzeitDisplayPage;
